import { SearchIcon } from 'assets';
import { Input } from 'components';
import Table from 'components/Table';
import { SectionBlock } from 'components/layout/SectionBlock';
import useMultipliersStore from 'pages/SettingsPage/store/useMultipliersStore';
import React, { useCallback, useEffect, useState } from 'react';
import useMultipliersTableColumns from './hooks/useMultipliersTableColumns';
import TablePagination from 'components/TablePagination';
import useDebounce from 'hooks/useDebounce';
import { AddMultiplier } from './components/AddMultiplier';
import { useSearchParams } from 'react-router-dom';

import './MulitpliersSettings.styles.scss';

const pageParamName = 'page';
const searchParamName = 'search';

const MulitpliersSettings = () => {
  const { getMultipliers, multipliers, pagination, isLoading, applyAndSearch, params } =
    useMultipliersStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const page = +(searchParams.get(pageParamName) || '') || 1;
  const search = searchParams.get(searchParamName) || '';

  const [searchString, setSearchString] = useState(search);

  useEffect(() => {
    getMultipliers(page, search);
  }, [page, search]);

  useEffect(() => {
    if (multipliers.length === 0 && searchString === '' && pagination.currentPage !== 1)
      onPageChange(page);
  }, [multipliers]);

  const columns = useMultipliersTableColumns();

  const onPageChange = (page: number) => {
    searchParams.set(pageParamName, page.toString());
    setSearchParams(searchParams);
  };

  const onSearchStringChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
  }, []);

  const debouncedSearchString = useDebounce(searchString, 500);

  useEffect(() => {
    if (!searchParamName || debouncedSearchString === search) return;

    if (!debouncedSearchString) searchParams.delete(searchParamName);
    else searchParams.set(searchParamName, debouncedSearchString);

    searchParams.delete(pageParamName);

    setSearchParams(searchParams);
  }, [debouncedSearchString]);

  return (
    <SectionBlock
      title='Multipliers'
      loading={isLoading}
      asideFromTitle={<AddMultiplier onMultiplierAdded={() => getMultipliers(page, search)} />}
      className='multipliers-page'
    >
      <Input
        prefixNode={<SearchIcon />}
        value={searchString}
        onChange={onSearchStringChange}
        placeholder='Search for a multiplier...'
        className={`search-input multipliers-page__search`}
      />
      <div className='multipliers-page__table-container'>
        <Table
          data={multipliers}
          prepareData={columns}
          noDataMessage={
            multipliers.length === 0 && debouncedSearchString !== ''
              ? 'There are no multipliers under that name.'
              : 'There are no multipliers added yet.'
          }
          hasScrollbar
        />
      </div>

      {pagination?.totalItems > 0 && (
        <TablePagination
          totalPages={pagination?.totalPages}
          currentPage={pagination?.currentPage}
          onPageChange={onPageChange}
          totalItems={pagination?.totalItems}
          pageSize={params.limit}
        />
      )}
    </SectionBlock>
  );
};

export default MulitpliersSettings;
